import { Interface } from "@ethersproject/abi"
import { BigNumber } from "@ethersproject/bignumber"
import { parseUnits } from "@ethersproject/units"
import type { BytesLike } from "ethers/lib/utils"
import { formatUnits, parseEther } from "ethers/lib/utils"
import invariant from "tiny-invariant"
import type { AbiInput, AbiItem } from "web3-utils"

import type { TallyActionArgs } from "createProposal/action/types"
import type { Account, ExecutableCallInput } from "query/graphql"
import { ExecutableCallType } from "query/graphql"
import { getInputTypesAndValues } from "createProposal/helpers/action"
import { jsonStringify } from "common/helpers/serialization"
import type { TokenBalances, TokenInfo } from "common/types/tokenBalances"
import TransferNativeAssetAction from "createProposal/action/TransferNativeAssetAction"
import { getAccountAddress } from "web3/helpers/accountId"
import { isSameAddress } from "web3/helpers/address"
import { getTallyRewardRecipient } from "createProposal/helpers/reward"

const recipientInput: AbiInput = {
  internalType: "address",
  name: "recipient",
  type: "address",
}

const amountInput: AbiInput = {
  internalType: "uint256",
  name: "amount",
  type: "uint256",
}

const transferAbiMethod: AbiItem = {
  constant: false,
  inputs: [recipientInput, amountInput],
  name: "transfer",
  outputs: [
    {
      internalType: "uint256",
      name: "amount",
      type: "uint256",
    },
  ],
  payable: false,
  stateMutability: "nonpayable",
  type: "function",
}

type RewardRecipient = Pick<Account, "address" | "picture" | "name"> & {
  rewardValue: string
}

type TransferErc20ActionArgs = {
  recipient?: string
  tokenInfo?: Pick<TokenInfo, "address" | "decimals">
  tallyRewardRecipient?: RewardRecipient
  rewardsRecipients?: RewardRecipient[]
  contributorFee?: number
  tallyFee?: number
} & TallyActionArgs

class TransferErc20Action extends TransferNativeAssetAction {
  recipient?: string
  tokenInfo?: Pick<TokenInfo, "address" | "decimals">
  rewardsRecipients?: RewardRecipient[]
  contributorFee?: number
  tallyFee?: number

  constructor({
    targetAddress,
    value,
    recipient,
    tokenInfo,
    tallyRewardRecipient,
    rewardsRecipients,
    contributorFee,
    tallyFee,
  }: TransferErc20ActionArgs) {
    super({
      targetAddress,
      value,
      tallyRewardRecipient,
      rewardsRecipients,
      contributorFee,
      tallyFee,
    })

    this.recipe = ExecutableCallType.Erc20transfer

    if (recipient) {
      this.recipient = recipient
    }

    if (tokenInfo) {
      this.tokenInfo = tokenInfo
    }
  }

  getSignature(): string {
    return "transfer(address,uint256)"
  }

  getValue(): string {
    return parseEther("0").toString()
  }

  getCalldata(): string {
    const { recipient, value } = this

    return this._getCalldata(recipient, value)
  }

  encodeRewardForBlockchain(rewardsRecipient: RewardRecipient) {
    const data = this._getCalldata(
      rewardsRecipient.address,
      rewardsRecipient.rewardValue,
    )

    const target = this.getTarget()

    const method = this.getSignature()
    invariant(
      typeof method === "string",
      "This action requires a single signature.",
    )

    const value = this.getValue()
    invariant(
      typeof value === "string",
      "This action requires a single signature.",
    )

    return {
      target,
      calldata: data,
      signature: method,
      value,
    }
  }

  _getCalldata(recipient: string | undefined, value: string): string {
    const { tokenInfo } = this

    if (!recipient || !tokenInfo) {
      return ""
    }

    const { decimals } = tokenInfo

    const bigValue = BigNumber.from(parseUnits(value, decimals))

    const toInputWithValue = {
      ...recipientInput,
      value: recipient,
    }
    const amountInputWithValue = {
      ...amountInput,
      value: bigValue.toString(),
    }

    const inputs = [toInputWithValue, amountInputWithValue]

    const { values } = getInputTypesAndValues(inputs)

    const name = "transfer"

    const abi = [transferAbiMethod]

    const proposalInterface = new Interface(jsonStringify(abi))

    return proposalInterface.encodeFunctionData(name, values)
  }

  static async from(
    executableAction: ExecutableCallInput,
    chainId: string,
    tokenBalances?: TokenBalances,
  ): Promise<TransferErc20Action> {
    const abi = [transferAbiMethod]
    const proposalInterface = new Interface(jsonStringify(abi))
    const decoded = proposalInterface.decodeFunctionData(
      "transfer",
      executableAction.calldata as BytesLike,
    )
    const recipient = decoded?.recipient ?? ""
    const amount = decoded?.amount ?? undefined
    const targetAddress = getAccountAddress(executableAction.target)
    const tokenInfo =
      tokenBalances?.tokens?.find((token) =>
        isSameAddress(token.tokenInfo.address, targetAddress),
      )?.tokenInfo ?? undefined
    const nextAction = new TransferErc20Action({
      recipient,
      targetAddress,
      tokenInfo,
      value: tokenInfo?.decimals
        ? formatUnits(amount, tokenInfo?.decimals)
        : "0",
      tallyRewardRecipient: getTallyRewardRecipient(chainId),
    })

    await nextAction.populateRewardsRecipients(executableAction)

    return nextAction
  }
}

export default TransferErc20Action
